import { vases } from '../data/vases';
import { DEFAULT_INTRO_FINISH_KEYS } from './finishTypes';
import { toWebpVariantUrl } from './optimizedImages';
import {
  createPageLayout,
  listPageLayoutsForAdmin,
  loadPageLayoutForAdmin,
  publishPageLayoutDraft,
  resolvePageLayoutDraft,
  resolvePageLayoutPublished,
  savePageLayoutDraft,
} from './pageLayoutsDraft';
import { DEFAULT_INTRO_VIEWER_PIPELINE_OVERRIDES } from './introViewerDefaults';
import type { ColourFinish } from '../types';
import type { HomepageLayoutContent } from '../components/sections/sectionRegistry';
import type { PublicRenderPipelineOverrides } from './publicRenderPipelines';

export const INTRO_PAGE_KEY = 'intro';
export const INTRO_PAGE_TITLE = 'Intro';
export const INTRO_VIEWER_BLOCK_ID = 'intro-viewer';

type IntroVariant = {
  modelPath: string;
  vaseId?: number;
  finishType: ColourFinish;
  selectedColors: string[];
  posterSrc: string;
  posterAlt: string;
};

const defaultVase =
  vases.find((entry) => typeof entry.modelPath === 'string' && entry.modelPath.trim().length > 0) ??
  null;

const DEFAULT_INTRO_VARIANT: IntroVariant = {
  modelPath: defaultVase?.modelPath ?? '',
  vaseId: defaultVase?.id,
  finishType: DEFAULT_INTRO_FINISH_KEYS[0] ?? 'shifting',
  selectedColors: ['#FFD700', '#9B30FF'],
  posterSrc: defaultVase ? toWebpVariantUrl(defaultVase.image, 'md') ?? defaultVase.image : '',
  posterAlt: defaultVase ? `${defaultVase.name} intro preview` : 'Intro model preview',
};

const LEGACY_INTRO_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  flows: [],
  flowState: {},
  sections: [
    {
      type: 'canvas',
      id: 'intro-stage',
      settings: {
        padding: 'none',
        paddingTop: 'none',
        paddingBottom: 'none',
        maxWidth: 'full',
        background: 'default',
        canvas: {
          minRows: 12,
          presentation: 'immersive',
          blocks: [
            {
              id: INTRO_VIEWER_BLOCK_ID,
              type: 'model-viewer',
              layout: { x: 1, y: 1, w: 24, h: 12 },
              content: {
                modelPath: DEFAULT_INTRO_VARIANT.modelPath,
                vaseId: DEFAULT_INTRO_VARIANT.vaseId,
                posterSrc: DEFAULT_INTRO_VARIANT.posterSrc,
                posterAlt: DEFAULT_INTRO_VARIANT.posterAlt,
                surface: 'immersive',
                publicLightingMode: 'hero',
                renderPresetId: 'hero',
                finishType: DEFAULT_INTRO_VARIANT.finishType,
                selectedColors: DEFAULT_INTRO_VARIANT.selectedColors,
              },
            },
            {
              id: 'intro-heading',
              type: 'text',
              layout: { x: 6, y: 9, w: 14, h: 1 },
              content: {
                text: 'Welcome to Eco3Dcor',
                presetKey: 'heroTitle',
              },
              style: {
                __template_slot: 'hero',
                role: 'heading',
              },
            },
            {
              id: 'intro-subheading',
              type: 'text',
              layout: { x: 7, y: 10, w: 12, h: 1 },
              content: {
                text: 'Experience our stunning collection of colour-shifting vases where artistry meets innovation',
                presetKey: 'heroSubtitle',
              },
              style: {
                __template_slot: 'hero',
                role: 'subheading',
              },
            },
            {
              id: 'intro-enter',
              type: 'button',
              layout: { x: 10, y: 11, w: 5, h: 1 },
              content: {
                label: 'Enter Gallery',
                actionMode: 'advanced',
                actionKind: 'navigation',
                advancedHref: '/home',
                pageKey: 'home',
                size: 'large',
                style: 'outlined',
              },
              style: {
                __template_slot: 'hero',
                role: 'primary-cta',
              },
            },
          ],
        },
      },
    },
  ],
};

export const DEFAULT_INTRO_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  flows: [],
  flowState: {},
  sections: [
    {
      type: 'canvas',
      id: 'intro-stage',
      settings: {
        padding: 'none',
        paddingTop: 'none',
        paddingBottom: 'none',
        maxWidth: 'full',
        background: 'default',
        canvas: {
          minRows: 14,
          presentation: 'immersive',
          blocks: [
            {
              id: INTRO_VIEWER_BLOCK_ID,
              type: 'model-viewer',
              layout: { x: 1, y: 1, w: 24, h: 14 },
              content: {
                modelPath: DEFAULT_INTRO_VARIANT.modelPath,
                vaseId: DEFAULT_INTRO_VARIANT.vaseId,
                posterSrc: DEFAULT_INTRO_VARIANT.posterSrc,
                posterAlt: DEFAULT_INTRO_VARIANT.posterAlt,
                surface: 'immersive',
                publicLightingMode: 'hero',
                renderPresetId: 'hero',
                finishType: DEFAULT_INTRO_VARIANT.finishType,
                selectedColors: DEFAULT_INTRO_VARIANT.selectedColors,
              },
            },
            {
              id: 'intro-heading',
              type: 'text',
              layout: { x: 5, y: 10, w: 16, h: 1 },
              content: {
                text: 'Welcome to Eco3Dcor',
                variant: 'hero-heading',
                presetKey: 'heroTitle',
              },
              style: {
                __template_slot: 'hero',
                role: 'heading',
              },
            },
            {
              id: 'intro-subheading',
              type: 'text',
              layout: { x: 6, y: 11, w: 14, h: 2 },
              content: {
                text: 'Experience our stunning collection of colour-shifting vases where artistry meets innovation',
                presetKey: 'heroSubtitle',
              },
              style: {
                __template_slot: 'hero',
                role: 'subheading',
              },
            },
            {
              id: 'intro-enter',
              type: 'button',
              layout: { x: 10, y: 13, w: 5, h: 1 },
              content: {
                label: 'Enter Gallery',
                actionMode: 'advanced',
                actionKind: 'navigation',
                advancedHref: '/home',
                pageKey: 'home',
                size: 'large',
                style: 'glass',
                variant: 'glass',
              },
              style: {
                backgroundColor: '#ffffff',
                backgroundOpacity: 0.18,
                borderColor: '#ffffff',
                borderWidth: 1,
                borderRadius: 10,
                textColor: '#ffffff',
                boxShadow: {
                  x: 0,
                  y: 12,
                  blur: 28,
                  spread: -12,
                  color: 'rgba(0, 0, 0, 0.55)',
                },
                hover: {
                  backgroundColor: '#ffffff',
                  backgroundOpacity: 0.28,
                  borderColor: '#ffffff',
                  textColor: '#ffffff',
                },
              },
            },
          ],
        },
      },
    },
  ],
};

let ensureIntroPagePromise: Promise<void> | null = null;
let introEnteredThisPageLoad = false;

export function hasEnteredIntro() {
  return introEnteredThisPageLoad;
}

export function markIntroEntered() {
  introEnteredThisPageLoad = true;
}

export function getIntroFallbackVariant(): IntroVariant {
  return DEFAULT_INTRO_VARIANT;
}

export function applyIntroVariantToLayout(
  layout: HomepageLayoutContent,
  variant: IntroVariant,
  viewerOverrides?: PublicRenderPipelineOverrides
): HomepageLayoutContent {
  const isIntroViewerBlock = (block: any) => {
    if (!block || block.type !== 'model-viewer') return false;
    if (block.id === INTRO_VIEWER_BLOCK_ID) return true;
    const content = block.content && typeof block.content === 'object' ? block.content : {};
    const surface = String(content.surface ?? '').toLowerCase();
    const lightingMode = String(content.publicLightingMode ?? '').toLowerCase();
    return surface === 'immersive' || surface === 'full-bleed' || lightingMode === 'hero';
  };

  return {
    ...layout,
    sections: layout.sections.map((section) => {
      const blocks = section.settings?.canvas?.blocks;
      if (!Array.isArray(blocks) || blocks.length === 0) return section;
      const hasIntroViewer = blocks.some(isIntroViewerBlock);
      if (!hasIntroViewer) return section;
      const nextBlocks = blocks.map((block) => {
        const blockStyle = block.style && typeof block.style === 'object' ? block.style as Record<string, unknown> : {};
        const role = typeof blockStyle.role === 'string' ? blockStyle.role : '';

        if (isIntroViewerBlock(block)) {
          const content = block.content && typeof block.content === 'object'
            ? (block.content as Record<string, unknown>)
            : {};
          const savedPipelineOverrides =
            content.publicPipelineOverrides && typeof content.publicPipelineOverrides === 'object'
              ? (content.publicPipelineOverrides as Record<string, unknown>)
              : {};
          return {
            ...block,
            content: {
              ...content,
              modelPath: variant.modelPath,
              vaseId: variant.vaseId,
              finishType: variant.finishType,
              selectedColors: variant.selectedColors,
              posterSrc: variant.posterSrc,
              posterAlt: variant.posterAlt,
              publicLightingMode: 'hero',
              renderPresetId: 'hero',
              publicPipelineOverrides: {
                ...DEFAULT_INTRO_VIEWER_PIPELINE_OVERRIDES,
                ...savedPipelineOverrides,
                ...(viewerOverrides ?? {}),
              },
              surface: 'immersive',
            },
          };
        }

        if (block.id === 'intro-heading' || role === 'heading') {
          return block;
        }

        if (block.id === 'intro-subheading' || role === 'subheading') {
          return block;
        }

        if (block.id === 'intro-enter' || role === 'primary-cta') {
          return {
            ...block,
            content: {
              ...(block.content ?? {}),
              actionMode: 'advanced',
              actionKind: 'navigation',
              advancedHref: '/home',
              pageKey: 'home',
            },
          };
        }

        return block;
      });
      return {
        ...section,
        settings: {
          ...(section.settings ?? {}),
          padding: 'none',
          paddingTop: 'none',
          paddingBottom: 'none',
          maxWidth: 'full',
          background: 'default',
          canvas: {
            ...(section.settings?.canvas ?? { minRows: 12, blocks: [] }),
            minRows: 14,
            minRowsMode: 'manual',
            presentation: 'immersive',
            blocks: nextBlocks,
          },
        },
      };
    }),
  };
}

function layoutsEqual(left: HomepageLayoutContent, right: HomepageLayoutContent) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isLegacyIntroLayout(layout: HomepageLayoutContent | null | undefined) {
  return Boolean(layout) && layoutsEqual(layout as HomepageLayoutContent, LEGACY_INTRO_LAYOUT_CONTENT);
}

export async function ensureIntroPageLayout() {
  if (ensureIntroPagePromise) return ensureIntroPagePromise;

  ensureIntroPagePromise = (async () => {
    const pagesRes = await listPageLayoutsForAdmin();
    if (pagesRes.error) throw pagesRes.error;

    const exists = (pagesRes.data ?? []).some((row) => row.page_key === INTRO_PAGE_KEY);
    if (exists) {
      const loadRes = await loadPageLayoutForAdmin(INTRO_PAGE_KEY);
      if (loadRes.error || !loadRes.data) {
        if (loadRes.error) throw loadRes.error;
        return;
      }

      const currentDraft = resolvePageLayoutDraft(loadRes.data, LEGACY_INTRO_LAYOUT_CONTENT);
      const currentPublished = resolvePageLayoutPublished(loadRes.data, currentDraft);

      if (isLegacyIntroLayout(currentDraft) && isLegacyIntroLayout(currentPublished)) {
        const saveRes = await savePageLayoutDraft(INTRO_PAGE_KEY, DEFAULT_INTRO_LAYOUT_CONTENT);
        if (saveRes.error) throw saveRes.error;

        const publishRes = await publishPageLayoutDraft(INTRO_PAGE_KEY);
        if ((publishRes as any)?.error) {
          throw (publishRes as any).error;
        }
      }
      return;
    }

    const { error } = await createPageLayout({
      pageKey: INTRO_PAGE_KEY,
      title: INTRO_PAGE_TITLE,
      draftContent: DEFAULT_INTRO_LAYOUT_CONTENT,
      publishedContent: DEFAULT_INTRO_LAYOUT_CONTENT,
    });

    if (error) {
      const message = String((error as any)?.message ?? '').toLowerCase();
      if (!message.includes('already exists') && !message.includes('duplicate')) {
        throw error;
      }
    }
  })();

  try {
    await ensureIntroPagePromise;
  } finally {
    ensureIntroPagePromise = null;
  }
}
